import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ReferenceService } from '../../../sunleon/data-access/http/reference.service'
import { TypeReferenceDto } from '../../../sunleon/data-access/http/dto/type-reference-dto'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { Subject, startWith, takeUntil } from 'rxjs'
import { CalepinageService } from '../../data-access/calepinage.service'
import { TypeGammePanneau } from '../../model/calepinage.types'
import { ParametreService } from 'src/app/parametre/data-access/http/parametre.service'
import {
  ONDULEURS_STRING,
  determinerGammeMateriel,
  filtrerCodes,
  isMaterielNziaExige,
} from '../../utils/gamme-materiel'

@Component({
  selector: 'app-form-capex-generalises',
  templateUrl: './form-capex-generalises.component.html',
  styleUrls: ['./form-capex-generalises.component.scss'],
  standalone:true,
  imports: [NgIf, FormsModule, NgForOf, ReactiveFormsModule, NgClass],
})

export class FormCapexGeneralisesComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup | null = null

  @Output()
  formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>()

  capexGeneralisesForm: FormGroup
  typeGammePanneaux: TypeReferenceDto[] = []
  filteredTypeGammePanneaux: TypeReferenceDto[] = []
  typeGammeOnduleurs: TypeReferenceDto[] = []
  filteredTypeGammeOnduleurs: TypeReferenceDto[] = []
  private readonly unsubscribe$ = new Subject<void>()
  private parentSubscriptionsRegistered = false
  private seuilPcreBat: number | null = null
  /** Catégorie du dernier contexte appliqué : les valeurs par défaut ne sont réimposées qu'à son changement. */
  private derniereCategorie: string | null = null

  constructor(
    private referenceService: ReferenceService,
    private calepinageService: CalepinageService,
    private parametreService: ParametreService,
    private formBuilder: FormBuilder) {

    this.capexGeneralisesForm = this.formBuilder.group({
      typeGammePanneau:['CRE', [Validators.required]],
      typeGammeOnduleur:['STRING', [Validators.required]],
      coffretDc: [false],
      paratonnerre: [false],
      aerodrome: [false],
      prixApportAffaireHtWc: [null],
      travauxDiversHT: [null],
      stationMeteo: [false]
    })

  }

  ngOnInit(): void {
    this.referenceService.getTypeGammePanneau().subscribe((data) => {
      this.typeGammePanneaux = data
      this.syncGammeMateriel(true)
    })
    this.parametreService.getParametreByCode('SEUIL_PCREBAT').subscribe((parametre) => {
      this.seuilPcreBat = parametre?.valeur != null ? Number(parametre.valeur) : null
      this.syncGammeMateriel()
    })
    // Publie la gamme sélectionnée pour la bulle récap calepinage (puissance unitaire CRE/FR).
    const gammeControl = this.capexGeneralisesForm.get('typeGammePanneau')
    gammeControl.valueChanges
      .pipe(startWith(gammeControl.value), takeUntil(this.unsubscribe$))
      .subscribe((gamme: TypeGammePanneau) => this.calepinageService.setTypeGammePanneau(gamme ?? 'CRE'))
    this.referenceService.getTypeGammeOnduleur().subscribe((data) => {
      this.typeGammeOnduleurs = data
      this.syncGammeMateriel(true)
    })
    this.initGammeOnduleur('typeGammeOnduleur', 'coffretDc','paratonnerre')
    this.registerParentSubscriptions()
    setTimeout(() => this.registerParentSubscriptions())
    this.formReady.emit(this.capexGeneralisesForm)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  initGammeOnduleur(gammeOnduleur: string,coffretDC: string,paratonnerre: string
  ) {
    this.capexGeneralisesForm.get(gammeOnduleur).valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((onduleur) => {
      const constCoffretDC = this.capexGeneralisesForm.get(coffretDC)
      const constParatonnerre = this.capexGeneralisesForm.get(paratonnerre)
      if (!ONDULEURS_STRING.includes(onduleur)) {
        constCoffretDC.setValue(false)
        constParatonnerre.setValue(false)
      }
    })
  }

  /**
   * Panneaux et onduleurs proposés, et valeurs imposées par défaut, selon le mode de valorisation,
   * le raccordement, le mécanisme de revente (AC) et la puissance totale calculée (VT)
   * (VIRYA_SUNL-16055, règles dans utils/gamme-materiel).
   */
  private syncGammeMateriel(listesChargees = false): void {
    const contexte = {
      modeValorisation: this.parentForm?.get('modevalorisation')?.get('modeValorisation')?.value ?? null,
      raccordementBt: this.parentForm?.get('raccordement')?.get('typeRaccordement')?.value !== 'HT',
      mecanismeReventeSurplus: this.parentForm?.get('modevalorisation')?.get('mecanismeReventeSurplus')?.value ?? null,
      puissanceTotaleKwc: this.parentForm?.get('raccordement')?.get('puissanceCentraleEstimee')?.value ?? null,
      seuilPcreBat: this.seuilPcreBat,
    }
    const gamme = determinerGammeMateriel(contexte)

    this.filteredTypeGammePanneaux = filtrerCodes(this.typeGammePanneaux, gamme.panneauxAutorises)
    this.filteredTypeGammeOnduleurs = filtrerCodes(this.typeGammeOnduleurs, gamme.onduleursAutorises, gamme.onduleursExclus)

    const categorie = `${isMaterielNziaExige(contexte)}|${contexte.raccordementBt}`
    const categorieChangee = categorie !== this.derniereCategorie
    this.derniereCategorie = categorie

    this.appliquerValeur('typeGammePanneau', this.filteredTypeGammePanneaux, gamme.panneauParDefaut, categorieChangee, listesChargees)
    this.appliquerValeur('typeGammeOnduleur', this.filteredTypeGammeOnduleurs, gamme.onduleurParDefaut, categorieChangee, listesChargees)
  }

  /** Impose la valeur par défaut au changement de contexte, ou quand la valeur courante n'est plus proposée. */
  private appliquerValeur(control: string, options: TypeReferenceDto[], parDefaut: string, categorieChangee: boolean, listesChargees: boolean): void {
    if (options.length === 0) {
      return
    }
    const courante = this.capexGeneralisesForm.get(control)?.value
    const couranteProposee = options.some((option) => option.code === courante)
    if ((categorieChangee && !listesChargees) || !couranteProposee) {
      const prochaine = options.some((option) => option.code === parDefaut) ? parDefaut : options[0].code
      if (prochaine !== courante) {
        this.capexGeneralisesForm.get(control)?.setValue(prochaine)
      }
    }
  }

  private registerParentSubscriptions(): void {
    if (this.parentSubscriptionsRegistered) {
      return
    }

    const raccordementControl = this.parentForm?.get('raccordement')?.get('typeRaccordement')
    const modeValorisationControl = this.parentForm?.get('modevalorisation')?.get('modeValorisation')
    if (!raccordementControl || !modeValorisationControl) {
      return
    }

    this.parentSubscriptionsRegistered = true
    raccordementControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.syncGammeMateriel()
    })

    modeValorisationControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.syncGammeMateriel()
    })

    const mecanismeReventeControl = this.parentForm?.get('modevalorisation')?.get('mecanismeReventeSurplus')
    mecanismeReventeControl?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.syncGammeMateriel()
    })

    const puissanceTotaleControl = this.parentForm?.get('raccordement')?.get('puissanceCentraleEstimee')
    puissanceTotaleControl?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.syncGammeMateriel()
    })

    this.syncGammeMateriel()
  }

}
